// =====================================================================
// OtaWs — broadcast OTA WebSocket protocol schemas (Task 1, 2026-06-30)
// =====================================================================
// The edge agent and master communicate over a WebSocket. The edge
// initiates OTA operations (edge→master); the master reports live
// progress and terminal outcomes (master→edge).
//
// Phase enum on OtaStatusMsgSchema is intentionally distinct from the
// OtaRunStatus persisted in Firestore: the WS phase is a fine-grained
// broadcast progress indicator, not the durable run state.
// =====================================================================

import { z } from 'zod';

// ---------------------------------------------------------------------------
// Shared: per-slave progress snapshot (embedded in ota_status)
// ---------------------------------------------------------------------------

const WsSlaveStatusSchema = z.object({
  slave_id: z.number().int(),
  received: z.number().int(),
  total: z.number().int(),
  digest_ok: z.boolean(),
  booted_version: z.string().nullable().optional(),
});

// ---------------------------------------------------------------------------
// edge → master messages
// ---------------------------------------------------------------------------

/** Kick off an OTA run. Carries full firmware metadata so the master can
 *  coordinate the broadcast without an additional Firestore read. */
export const OtaStartMsgSchema = z.object({
  type: z.literal('ota_start'),
  run_id: z.string().min(1),
  total_bytes: z.number().int().positive(),
  sha256: z.string().min(1),
  signature_hex: z.string().min(1),
  version: z.string().min(1),
  version_idx: z.number().int().nonnegative(),
  target_type: z.string().min(1),
});
export type ParsedOtaStartMsg = z.infer<typeof OtaStartMsgSchema>;

/** Request current OTA run state from the master. */
export const OtaQueryMsgSchema = z.object({
  type: z.literal('ota_query'),
  run_id: z.string().min(1),
});
export type ParsedOtaQueryMsg = z.infer<typeof OtaQueryMsgSchema>;

/** Request master to activate (reboot) slaves after a successful verify. */
export const OtaActivateMsgSchema = z.object({
  type: z.literal('ota_activate'),
  run_id: z.string().min(1),
});
export type ParsedOtaActivateMsg = z.infer<typeof OtaActivateMsgSchema>;

/** Cancel an in-flight OTA run. */
export const OtaCancelMsgSchema = z.object({
  type: z.literal('ota_cancel'),
  run_id: z.string().min(1),
});
export type ParsedOtaCancelMsg = z.infer<typeof OtaCancelMsgSchema>;

// ---------------------------------------------------------------------------
// master → edge messages
// ---------------------------------------------------------------------------

/** Live progress update from the master — phase + per-slave digest snapshot. */
export const OtaStatusMsgSchema = z.object({
  type: z.literal('ota_status'),
  run_id: z.string().min(1),
  /**
   * Transfer lifecycle phase.
   *
   * The window-at-a-time master (fh-iot, 2026-07) emits only:
   *   `broadcasting` → `verified_awaiting_confirm` → `activating`
   * because verify and repair are folded into the per-window loop rather than
   * being separate global passes.
   *
   * `verifying` / `repairing` are retained for back-compat with the retired
   * flood master — do NOT drop them, an older master still emits them.
   *
   * NOTE: `activating` was missing here while the master already emitted it,
   * so `OtaWsServerMsgSchema.safeParse` failed and every status frame during
   * activation was SILENTLY DISCARDED by master-ws-client's fall-through.
   */
  phase: z.enum([
    'broadcasting',
    'verifying',
    'repairing',
    'verified_awaiting_confirm',
    'activating',
  ]),
  /** Per-slave progress snapshot. Empty during the initial broadcast phase. */
  slaves: z.array(WsSlaveStatusSchema),
  /** Counts how many repair rounds have been attempted so far. */
  repair_round: z.number().int().nonnegative(),
});
export type ParsedOtaStatusMsg = z.infer<typeof OtaStatusMsgSchema>;

/** Emitted when a specific slave successfully reboots into the new firmware. */
export const OtaActivatedMsgSchema = z.object({
  type: z.literal('ota_activated'),
  run_id: z.string().min(1),
  slave_id: z.number().int(),
  /**
   * The fw id the slave reports in its own `evt:boot` after rebooting into the
   * new image (e.g. "6fe4152-597b"), so this reflects what ACTUALLY came up.
   * Falls back to the `version` string the edge sent in `ota_start` if the id
   * cannot be parsed out of the boot line.
   */
  booted_version: z.string().min(1),
});
export type ParsedOtaActivatedMsg = z.infer<typeof OtaActivatedMsgSchema>;

/** Final outcome — emitted once when the OTA run reaches a terminal state. */
export const OtaTerminalMsgSchema = z.object({
  type: z.literal('ota_terminal'),
  run_id: z.string().min(1),
  result: z.enum(['done', 'failed']),
  /**
   * Set ONLY when the run actually failed, identifying the slave involved.
   * (It used to be populated on success too, with the last slave activated,
   * which read as "slave N failed" on a fully successful rollout.)
   */
  failed_slave_id: z.number().int().optional(),
  /**
   * Error token when `result === 'failed'`. Kept as a free-form string so a
   * firmware-side addition cannot break parsing, but the master's current set
   * is:
   *   `verify_failed`     — a window never converged / a poll or flush went
   *                         unanswered past its retry budget
   *   `activate_timeout`  — an activated slave never reported a boot-confirm;
   *                         remaining slaves were left on their old image
   *   `no_participants`   — plan built with an empty participant list
   *   `bad_chunk_size`    — chunk_size would overflow the 64-chunk window bitmap
   *   `bad_size`          — zero or oversized image
   *   `cancelled`         — operator `ota_cancel`
   *   `not_quiesced`      — fleet would not enter maintenance before the broadcast
   *   `source_eof`        — the image producer went away mid-transfer
   *   `timeout`           — the image producer stalled
   *   `txq_backpressure`  — unrecoverable CAN TX backpressure
   */
  error: z.string().optional(),
});
export type ParsedOtaTerminalMsg = z.infer<typeof OtaTerminalMsgSchema>;

// ---------------------------------------------------------------------------
// Discriminated union: master→edge server messages
// ---------------------------------------------------------------------------

export const OtaWsServerMsgSchema = z.discriminatedUnion('type', [
  OtaStatusMsgSchema,
  OtaActivatedMsgSchema,
  OtaTerminalMsgSchema,
]);
export type ParsedOtaWsServerMsg = z.infer<typeof OtaWsServerMsgSchema>;
